// src/hooks/useSubscriptionTier.js
// Shared subscription-tier check for SubscriptionGuard.jsx and SideMenu.js -
// previously each did its own inline role-decode + /api/billing/subscription
// fetch (SubscriptionGuard only); this consolidates both into one place so
// SideMenu doesn't duplicate it.
//
// "Duelist" (the free-forever plan - Arena/Duels/challenges, unlimited
// post-trade review, 5/mo AI Analyst) has no subscription_plans row or
// user_subscriptions record of its own - it's simply "authenticated, no
// active paid subscription", the exact same state backend/auth.py's
// FREE_TIER_PREFIXES already treats as free for duels/investments/
// playbooks/AI/demo-trades. This hook's 'free' return value IS that state;
// there's nothing else to check.
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// The /app/* sub-pages a Duelist (free-tier) user can reach - every
// other /app/* route stays behind SubscriptionGuard's normal block. See
// FREE_TIER_APP_EXACT_PATHS below for the Dashboard/Learning pair, which
// can't safely live in this prefix-matched list.
// /arena, /duels/profile, /duels/backtesting need no entry here - they're
// public top-level routes outside /app/* entirely (see App.js), reachable
// even logged out.
export const FREE_TIER_APP_PATHS = [
  '/app/demo-trades', '/app/playbook', '/app/ai-chat', '/app/billing',
  // Pre-trade planning (the plan popup, plans list, reconcile, plan-vs-
  // execution) - a discipline tool, not premium analytics. Kept free to
  // match backend/auth.py FREE_TIER_PREFIXES ('/api/trade-plans'); a
  // Duelist shouldn't hit a paywall on planning a trade before entry.
  // startsWith covers /app/trade-plans/reconcile/... and .../:id/execution.
  '/app/trade-plans',
  // Creator host-request + control pages (challenge_routes.py) - "challenges"
  // was always part of the free Duelist tier spec alongside duels, this
  // pair was just never added here when the tier launched, leaving both
  // unreachable behind the paywall despite being meant to be free.
  '/app/challenges',
  // Drills (backend drills.py): replays of the user's own misplayed trades, free like duels (FREE_TIER_PREFIXES '/api/drills').
  '/app/drills',
  // Help & support chat (SUP-01): matches backend FREE_TIER_PREFIXES
  // '/api/support' - a customer whose trial ended must still reach support.
  '/app/support',
  // Nummius Academy: matches backend FREE_TIER_PREFIXES '/api/edu'. Who can open it is decided by the Academy's
  // own gate (edu/AcademyGate + backend edu._gate: superadmin, preview list, or switched on for everyone).
  '/app/edu',
];

// Exact-match-only free routes - can't go in FREE_TIER_APP_PATHS above
// because that list also matches any path.startsWith(p + '/'), and '/app/'
// is a prefix of literally every /app/* route; adding '/app' there would
// silently free the entire paid app. Both routes here have no nested
// sub-paths, so exact match is also all they need.
// '/app' = Dashboard (InvestmentsGrid.js): the user's own trade summary,
// cheap own-data widgets, the natural first thing a new signup should see
// before they've logged anything worth paying to analyze.
// '/app/learning' = a fully static page (candlestick-signals.html in an
// iframe), no backend calls at all - no reason this was ever paid-gated.
export const FREE_TIER_APP_EXACT_PATHS = ['/app', '/app/learning'];

export function isFreeTierAppPath(path) {
  return FREE_TIER_APP_EXACT_PATHS.includes(path) ||
    FREE_TIER_APP_PATHS.some((p) => path === p || path.startsWith(p + '/'));
}

// Returns { tier, reason }. tier: 'loading' | 'full' | 'free'. 'full' =
// role-bypassed (superadmin/family/admin) or an active paid subscription;
// 'free' = authenticated with no active paid subscription (Duelist). Only
// ever called from inside the logged-in app shell (App.js already
// redirects a logged-out visitor to /login before either consumer mounts),
// so there's no "logged out" case to report here. reason mirrors
// /api/billing/subscription's access_reason (no_subscription/
// trial_expired/past_due/canceled/blocked) - only meaningful once
// tier === 'free', kept alongside tier (not folded into it) so
// SubscriptionGuard can show the specific reason while SideMenu can just
// ignore it and check tier alone.
export default function useSubscriptionTier() {
  const [tier, setTier] = useState('loading');
  const [reason, setReason] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setTier('full'); return; } // no token - let the caller's own auth check handle it, don't restrict

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (['superadmin', 'family', 'admin'].includes(payload.role)) { setTier('full'); return; }
    } catch (e) { /* fall through to the real check below */ }

    let cancelled = false;
    fetch(API + '/api/billing/subscription', { headers: { Authorization: 'Bearer ' + token } })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.access_allowed) { setTier('full'); return; }
        setReason(data.access_reason || 'no_subscription');
        setTier('free');
      })
      .catch(() => { if (!cancelled) setTier('full'); }); // fail open, same as SubscriptionGuard's existing behavior
    return () => { cancelled = true; };
  }, []);

  return { tier, reason };
}
